import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getBudgetBoard, getBudgetPlanItems } from './viewServices/budget_api';
import { fetchFinancialFlows } from 'views/finance/moduleServices/flow_requests';
import { useFinanceProfile } from '../moduleServices/finance_profilecontext';
import FilterBudgetBoard from './viewComponents/Board/FilterBudgetBoard';
import CardBudgetConsumed from './viewComponents/Board/CardBudgetConsumed';
import CardBudgetCategories from './viewComponents/Board/CardBudgetCategories';
import CardBudgetDynamicTable from './viewComponents/Board/CardBudgetDynamicTable';
import { toast } from 'react-hot-toast';

function getYearMonth(date) {
  const y = date.getFullYear().toString();
  const m = ("0" + (date.getMonth() + 1)).slice(-2);
  return [y, m];
}

const BudgetBoard = ({ yearsRange }) => {
  const { profile } = useFinanceProfile();
  const defaultClassL0 = profile?.default_categ_l0 || 'All';

  const today = new Date();
  const [defaultYear, defaultMonth] = getYearMonth(today);
  
  const [loading, setLoading] = useState(false);
  const [boardData, setBoardData] = useState(null);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [flows, setFlows] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [budgets, setBudgets] = useState([]); // holds BudgetPlanItems
  
  // Filter states
  const [selectedType, setSelectedType] = useState('Out');
  const [selectedClassL0, setSelectedClassL0] = useState(defaultClassL0);
  const [selectedYears, setSelectedYears] = useState(defaultYear);
  const [selectedMonths, setSelectedMonths] = useState(defaultMonth);

  // Build filters object from states
  const filters = useMemo(() => ({
    type: selectedType,
    years: selectedYears,
    months: selectedMonths,
    l0: selectedClassL0 === 'All' ? 'All' : selectedClassL0,
  }), [selectedType, selectedYears, selectedMonths, selectedClassL0]);

  const fetchBoardData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBudgetBoard(filters);
      setBoardData(response.data);
    } catch (error) {
      console.error('Error fetching budget board:', error);
      toast.error('Error loading budget board');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchFlowsData = useCallback(async () => {
    setLoadingFlows(true);
    try {
      const limit = 5000;
      let offset = 0;
      let allFlows = [];
      let hasMore = true;

      const params = { years: selectedYears, months: selectedMonths, limit, offset };
      while (hasMore) {
        const data = await fetchFinancialFlows(params);
        allFlows = [...allFlows, ...data];
        hasMore = data.length === limit;
        offset += limit;
        params.offset = offset;
      }

      setFlows(allFlows);
    } catch (error) {
      console.error('Error fetching flows for budget board:', error);
      toast.error('Error loading flows');
      setFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  }, [selectedYears, selectedMonths]);

  const fetchBudgetsData = useCallback(async () => {
    setLoadingBudgets(true);
    try {
      const limit = 5000;
      let offset = 0;
      let allBudgets = [];
      let hasMore = true;

      const params = {
        years: selectedYears,
        months: selectedMonths,
        budget_type: selectedType,
        limit,
        offset,
      };
      if (selectedClassL0 && selectedClassL0 !== 'All') {
        params.class_l0 = selectedClassL0;
      }

      while (hasMore) {
        const response = await getBudgetPlanItems(params);
        const results = response?.data?.results || [];
        allBudgets = [...allBudgets, ...results];
        hasMore = results.length === limit;
        offset += limit;
        params.offset = offset;
      }

      setBudgets(allBudgets);
    } catch (error) {
      console.error('Error fetching budgets for budget board:', error);
      toast.error('Error loading budgets');
      setBudgets([]);
    } finally {
      setLoadingBudgets(false);
    }
  }, [selectedYears, selectedMonths, selectedType, selectedClassL0]);

  useEffect(() => {
    fetchBoardData();
    fetchFlowsData();
    fetchBudgetsData();
  }, [fetchBoardData, fetchFlowsData, fetchBudgetsData]);

  useEffect(() => {
    setSelectedClassL0(defaultClassL0);
  }, [defaultClassL0]);

  // Extract unique years and categories from boardData
  const yearsOpts = useMemo(() => {
    if (yearsRange && yearsRange.length) {
      return yearsRange;
    }
    if (!boardData?.filters_applied?.years) return [defaultYear];
    return [...new Set([defaultYear, ...boardData.filters_applied.years])].sort().map(String);
  }, [boardData, defaultYear, yearsRange]);

  const classL0Opts = useMemo(() => {
    if (!boardData?.by_l0) return [];
    return boardData.by_l0.map(item => item.l0).sort();
  }, [boardData]);

  const filterLabel = selectedClassL0;

  const filteredFlows = useMemo(() => {
    return (flows || []).filter(flow => {
      if (selectedClassL0 && selectedClassL0 !== 'All' && flow.categ_l0 !== selectedClassL0) return false;
      if (selectedType && flow.flow_type !== selectedType) return false;
      return true;
    });
  }, [flows, selectedClassL0, selectedType]);

  return (
    <div className="p-0 grid grid-cols-1 md:grid-cols-2 gap-2 w-full items-stretch">
      <div className="flex flex-col min-h-0">
        <FilterBudgetBoard
          categories={classL0Opts}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedClassL0={selectedClassL0}
          setSelectedClassL0={setSelectedClassL0}
          yearsOpts={yearsOpts}
          selectedYears={selectedYears}
          setSelectedYears={setSelectedYears}
          selectedMonths={selectedMonths}
          setSelectedMonths={setSelectedMonths}
        />
        <div className="flex-1 flex min-h-0">
          <CardBudgetConsumed
            boardData={boardData}
            loading={loading}
            filterLabel={filterLabel}
            selectedType={selectedType}
            flows={filteredFlows}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
          />
        </div>
      </div>

      <CardBudgetCategories
        boardData={boardData}
        loading={loading}
        filterLabel={filterLabel}
        selectedType={selectedType}
      />

      <div className="md:col-span-2">
        <CardBudgetDynamicTable
          flows={filteredFlows}
          budgets={budgets}
          loading={loadingFlows || loadingBudgets}
          selectedFlowCategL0={filterLabel}
          selectedFlowType={selectedType}
          setSelectedFlowType={setSelectedType}
        />
      </div>
    </div>
  );
};

export default BudgetBoard;
